import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Stack, Chip, Alert, CircularProgress, Button } from '@mui/material';
import { apiService } from '../../services/api';

export default function AboFeaturesPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadState = async () => {
      try {
        const data = await apiService.get('/api/feature-entitlements');
        setState(data);
      } catch (err) {
        setError(err?.message || 'Feature-Status konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    loadState();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const entitlements = state?.entitlements || {};
  const enabledEntries = Object.entries(entitlements).filter(([, enabled]) => Boolean(enabled));
  const disabledEntries = Object.entries(entitlements).filter(([, enabled]) => !enabled);
  const billingMode = state?.billing?.mode || 'free';
  const billingEnabled = Boolean(state?.billing?.enabled);
  const checkoutEnabled = Boolean(state?.billing?.checkoutEnabled);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Abo & Features</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Überblick über Planstatus, Feature-Freischaltungen und den nächsten sinnvollen Ausbauschritt.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Planmodus</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>{billingMode}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Aktive Features</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{enabledEntries.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Gesperrte Features</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{disabledEntries.length}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Aktueller Plan</Typography>
            <Chip label={billingMode} color="primary" />
            <Chip label={billingEnabled ? 'Billing aktiv' : 'Billing aus'} color={billingEnabled ? 'success' : 'default'} />
          </Stack>
          <Typography variant="body2" color="text.secondary">{state?.billing?.note || 'Alpha-/Free-Mode aktiv.'}</Typography>
          {!checkoutEnabled ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Checkout ist aktuell deaktiviert. Planwechsel werden derzeit manuell freigeschaltet.
            </Alert>
          ) : null}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Freigeschaltete Funktionen</Typography>
          <Stack spacing={1}>
            {Object.keys(entitlements).length > 0 ? Object.entries(entitlements).map(([key, enabled]) => (
              <Box key={key} sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>{key}</Typography>
                <Chip label={enabled ? 'aktiv' : 'inaktiv'} color={enabled ? 'success' : 'default'} />
              </Box>
            )) : (
              <Typography color="text.secondary">Keine Feature-Informationen verfügbar.</Typography>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={!checkoutEnabled}>Upgrade starten</Button>
            <Button variant="text" disabled>Rechnungen anzeigen (bald)</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
